import {
  Box,
  Paper,
  Toolbar,
  Typography
} from "@mui/material";
import { t } from "i18next";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { createCapture } from "../../api/captures/capture";
import { upsertCaptureLocation } from "../../api/captures/captureLocation";
import AddCaptureForm from "../../components/Captures/AddCaptureForm";
import { setLoading } from "../../store/UI";
import { CreateCaptureForm } from "../../types/forms/capture";
import { CreateCaptureRequest } from "../../types/model/capture";
import { CaptureLocationRequest } from "../../types/model/captureLocation";
import { enqueueSnackbar } from "notistack";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";

const AddCapture = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleError = useErrorHandler();

  const [searchParams] = useSearchParams();
  const [crs, setCrs] = useState<"wgs84" | "etrs89">("etrs89");

  const queryEntityId = searchParams.get("entityId");
  const queryManagementUnitId = searchParams.get("managementUnitId");

  const entityIdFromQuery = queryEntityId ? Number.parseInt(queryEntityId, 10) : undefined;
  const managementUnitIdFromQuery = queryManagementUnitId ? Number.parseInt(queryManagementUnitId, 10) : undefined;

  const handleCrsChange = (newCrs: "etrs89" | "wgs84") => {
    setCrs(newCrs);
  };

  const handleSaveAction = async (
    values: CreateCaptureForm,
    chosenEntityId?: number | undefined
  ) => {
    try {
      dispatch(setLoading(true));

      const entityIdParam = entityIdFromQuery ?? chosenEntityId;
      if (!entityIdParam) {
        enqueueSnackbar(t("generic.errors.entity-id-required"), { variant: "error" });
        return;
      }

      const capturePayload : CreateCaptureRequest = {
        managementUnitId: managementUnitIdFromQuery ?? values.managementUnitId,
        name: values.name,
        aquaEpsDesignation: values.aquaEpsDesignation ?? "",
        entityDesignation: values.entityDesignation ?? "",
        licensingDesignation: values.licensingDesignation ?? "",
        licensingDesignationSystem: values.licensingDesignationSystem ?? null,
      };

      const coords = values.coordinates;
      let coordinatesRequest: CaptureLocationRequest["coordinates"];

      if (coords) {
        if ("xETRS" in coords && "yETRS" in coords) {
          coordinatesRequest = {
            crs: "ETRS89",
            xETRS: Number(coords.xETRS),
            yETRS: Number(coords.yETRS),
            zETRS: Number(coords.zETRS),
          };
        } else if ("longitude" in coords && "latitude" in coords) {
          coordinatesRequest = {
            crs: "WGS84",
            longitude: Number(coords.longitude),
            latitude: Number(coords.latitude),
            elevation: Number(coords.elevation),
          };
        }
      }

      const locationPayload = {
        coordinates: coordinatesRequest,
      };

      const data = await createCapture(entityIdParam, capturePayload);

      try {
        await upsertCaptureLocation(data.id, locationPayload);
        navigate(`/captures/${data.id}`, { replace: true });
      } catch (error) {
        handleError(error);
      }
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
          <Toolbar>
            <Typography sx={{ flex: "1 1 100%" }} variant="h4">
              {t("management-units.management-unit.captures.add-new")}
            </Typography>
          </Toolbar>

          <AddCaptureForm 
            onSubmit={handleSaveAction}
            entityId={entityIdFromQuery}
            managementUnitId={managementUnitIdFromQuery}
            crs={crs}
            onCrsChange={handleCrsChange}
          />
        </Paper>
      </Box>
    </div>
  );
};

export default AddCapture;